import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Mail, 
  Phone, 
  MapPin,
  Send,
  Loader2,
  Linkedin,
  Instagram
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We will respond shortly.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const socialLinks = [
    { name: 'LinkedIn', icon: Linkedin, url: 'https://linkedin.com/company/uiehec' },
    { name: 'Instagram', icon: Instagram, url: 'https://instagram.com/uiehec' },
    { name: 'X', icon: 'X', url: 'https://x.com/uiehec' },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-[#0E285B] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Mail className="h-6 w-6 text-[#F5A623]" />
              <span className="text-[#F5A623] font-medium uppercase tracking-wider text-sm">
                Contact Us
              </span>
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              Get in Touch
            </h1>
            <p className="text-slate-300">
              Have questions about UIEHEC or membership? We're here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0E285B] mb-8">
                Head Office
              </h2>
              
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 bg-[#0E285B] rounded-sm flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-[#F5A623]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0E285B] mb-1">Address</h3>
                    <p className="text-slate-600">
                      Union of International Education and Higher Education Centers (UIEHEC)<br />
                      Heinrichsallee 18<br />
                      52062 Aachen<br />
                      Germany
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 bg-[#0E285B] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-[#F5A623]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0E285B] mb-1">Email</h3>
                    <p className="text-slate-600">
                      <a href="mailto:info@uiehec.org" className="text-[#F5A623] hover:underline">
                        info@uiehec.org
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 bg-[#0E285B] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-[#F5A623]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0E285B] mb-1">Phone</h3>
                    <p className="text-slate-600">
                      <a href="tel:+4916059429555" className="text-[#F5A623] hover:underline">
                        +49 160 594 29 55
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div>
                <h3 className="font-semibold text-[#0E285B] mb-4">Social Media</h3>
                <div className="flex gap-4">
                  {socialLinks.map((social) => {
                    const isX = social.icon === 'X';
                    const Icon = isX ? null : social.icon;
                    return (
                      <a
                        key={social.name}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-10 w-10 bg-[#0E285B] rounded-sm flex items-center justify-center hover:bg-[#F5A623] transition-colors group"
                        title={social.name}
                      >
                        {isX ? (
                          <span className="text-white group-hover:text-[#0E285B] font-bold text-lg">𝕏</span>
                        ) : (
                          <Icon className="h-5 w-5 text-white group-hover:text-[#0E285B]" />
                        )}
                      </a>
                    );
                  })}
                </div>
              </div>

              {/* Map */}
              <div className="mt-8">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2521.8!2d6.08!3d50.775!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTDCsDQ2JzMwLjAiTiA2wrAwNC00OC4wIkU!5e0!3m2!1sen!2sde!4v1600000000000!5m2!1sen!2sde"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  className="rounded-sm"
                  title="UIEHEC Location"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0E285B] mb-8">
                Send us a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="bg-white p-8 rounded-sm shadow-sm border border-slate-100">
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-[#0E285B]">
                        Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="mt-2 h-11 border-slate-200 focus:ring-[#F5A623] focus:border-[#F5A623]"
                        placeholder="Your name"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#0E285B]">
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="mt-2 h-11 border-slate-200 focus:ring-[#F5A623] focus:border-[#F5A623]"
                        placeholder="your@email.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-[#0E285B]">
                      Subject
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="mt-2 h-11 border-slate-200 focus:ring-[#F5A623] focus:border-[#F5A623]"
                      placeholder="What is this regarding?"
                      data-testid="contact-subject-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#0E285B]">
                      Message <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className="mt-2 min-h-[150px] border-slate-200 focus:ring-[#F5A623] focus:border-[#F5A623]"
                      placeholder="Your message..."
                      data-testid="contact-message-textarea"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#0E285B] text-white hover:bg-[#0E285B]/90 h-11"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Send Message
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
